import api from './api';

// File size limits (in bytes)
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;  // 10MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;  // 50MB
const MAX_VOICE_SIZE = 5 * 1024 * 1024;   // 5MB

const API_URL = process.env.REACT_APP_API_URL || '/api';
const getApiUrl = () => {
  const url = process.env.REACT_APP_API_URL || '/api';
  // Ensure it ends with /api if it's a full URL
  if (url.startsWith('http')) {
    return url.includes('/api') ? url : `${url}/api`;
  }
  return url;
};

// Upload using XMLHttpRequest (more reliable for file uploads)
const uploadWithXHR = (url, formData) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fullUrl = `${getApiUrl()}${url}`;
    console.log('[XHR Upload] Starting:', fullUrl);

    xhr.open('POST', fullUrl);

    const token = localStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    // Let browser set Content-Type with boundary - DO NOT set it manually

    xhr.withCredentials = true;

    // Track upload progress
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        console.log('[XHR Upload] Progress:', e.loaded, '/', e.total);
      }
    };

    xhr.onload = () => {
      console.log('[XHR Upload] Complete:', xhr.status, xhr.responseText?.substring(0, 100));
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          reject(new Error(data.error || 'Upload failed'));
        }
      } catch (e) {
        reject(new Error('Invalid response: ' + xhr.responseText));
      }
    };

    xhr.onerror = () => {
      console.log('[XHR Upload] Error');
      reject(new Error('Network error'));
    };

    xhr.onabort = () => {
      console.log('[XHR Upload] Aborted');
      reject(new Error('Upload aborted'));
    };

    console.log('[XHR Upload] Sending formData, entries:', Array.from(formData.entries()).map(e => [e[0], e[1] instanceof File ? e[1].name + '(' + e[1].size + ')' : e[1]]));
    xhr.send(formData);
  });
};

// Validate file before upload
const validateMediaFile = (file) => {
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');

  if (isImage && file.size > MAX_IMAGE_SIZE) {
    throw new Error('Image file too large. Maximum size is 10MB');
  }
  if (isVideo && file.size > MAX_VIDEO_SIZE) {
    throw new Error('Video file too large. Maximum size is 50MB');
  }
  if (!isImage && !isVideo) {
    throw new Error('Unsupported file type. Only images and videos are allowed');
  }
};

const validateVoiceFile = (file) => {
  if (file.size > MAX_VOICE_SIZE) {
    throw new Error('Voice file too large. Maximum size is 5MB');
  }
};

export const postApi = {
  // Posts
  createPost: async (data) => {
    console.log('[createPost] Sending data:', JSON.stringify(data));
    return await api.post('/posts', data);
  },
  getPost: async (id) => await api.get(`/posts/${id}`),
  getFeed: async (limit = 20, offset = 0) =>
    await api.get(`/posts/feed?limit=${limit}&offset=${offset}`),

  search: async (query, limit = 20, offset = 0) =>
    await api.get(`/posts/search?q=${encodeURIComponent(query)}&limit=${limit}&offset=${offset}`),
  getUserPosts: async (userId, limit = 20, offset = 0) =>
    await api.get(`/users/${userId}/posts?limit=${limit}&offset=${offset}`),
  updatePost: async (id, data) => await api.put(`/posts/${id}`, data),
  deletePost: async (id) => await api.delete(`/posts/${id}`),
  sharePost: async (postId, content = '', visibility = 'public') =>
    await api.post(`/posts/${postId}/share`, { content, visibility }),

  // Media upload
  uploadPostMedia: async (postId, file) => {
    validateMediaFile(file);
    const formData = new FormData();
    formData.append('media', file);
    // Use fetch to bypass axios issues
    return await uploadWithXHR(`/posts/${postId}/media`, formData);
  },

  // Voice upload
  uploadPostVoice: async (postId, audioBlob) => {
    validateVoiceFile(audioBlob);
    const formData = new FormData();
    formData.append('voice', audioBlob, 'voice.webm');
    // Use fetch to bypass axios issues
    return await uploadWithXHR(`/posts/${postId}/voice`, formData);
  },
  deletePostVoice: async (postId) => await api.delete(`/posts/${postId}/voice`),

  uploadCommentVoice: async (commentId, audioBlob) => {
    validateVoiceFile(audioBlob);
    const formData = new FormData();
    formData.append('voice', audioBlob, 'voice.webm');
    // Use fetch to bypass axios issues
    return await uploadWithXHR(`/comments/${commentId}/voice`, formData);
  },
  deleteCommentVoice: async (commentId) => await api.delete(`/comments/${commentId}/voice`),

  // Comments
  createComment: async (postId, data) =>
    await api.post(`/posts/${postId}/comments`, data),
  getPostComments: async (postId) =>
    await api.get(`/posts/${postId}/comments`),
  getBatchComments: async (postIds) =>
    await api.get(`/comments/batch?postIds=${postIds.join(',')}`),
  getComment: async (commentId) =>
    await api.get(`/comments/${commentId}`),
  
  updateComment: async (id, content) =>
    await api.put(`/comments/${id}`, { content }),
  deleteComment: async (id) => await api.delete(`/comments/${id}`),

  // Interactions
  likePost: async (postId) =>
    await api.post('/interactions', {
      target_type: 'post',
      target_id: postId,
      interaction_type: 'like'
    }),
  unlikePost: async (postId) =>
    await api.delete('/interactions', {
      data: { target_type: 'post', target_id: postId }
    }),
  likeComment: async (commentId) =>
    await api.post('/interactions', {
      target_type: 'comment',
      target_id: commentId,
      interaction_type: 'like'
    }),
  unlikeComment: async (commentId) =>
    await api.delete('/interactions', {
      data: { target_type: 'comment', target_id: commentId }
    }),

  // Get likes
  getPostLikes: async (postId, limit = 100) =>
    await api.get(`/posts/${postId}/likes?limit=${limit}`),

  // Link preview
  fetchLinkPreview: async (url) =>
    await api.get(`/link-preview?url=${encodeURIComponent(url)}`),
};
